import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  clientApiService,
  ChangeOrderDetail,
} from "../../services/client-api.service";
import { SignaturePad } from "../../components/documents/SignaturePad";

const STATUS_LABELS: Record<string, { label: string; className: string }> = {
  pending: { label: "⏳ Pendiente de tu aprobación", className: "bg-yellow-100 text-yellow-700" },
  client_approved: { label: "✅ Aprobado por ti", className: "bg-green-100 text-green-700" },
  approved: { label: "✅ Aprobado", className: "bg-green-100 text-green-700" },
  rejected: { label: "❌ Rechazado", className: "bg-red-100 text-red-700" },
  executed: { label: "🔨 Ejecutado", className: "bg-blue-100 text-blue-700" },
};

const CATEGORY_LABELS: Record<string, string> = {
  structural: "Estructural",
  finishes: "Acabados",
  systems: "Sistemas",
  layout: "Distribución",
  other: "Otro",
};

const PRIORITY_LABELS: Record<string, string> = {
  low: "Baja",
  medium: "Media",
  high: "Alta",
  critical: "Crítica",
};

function formatCurrency(n: number) {
  return new Intl.NumberFormat("es-MX", {
    style: "currency",
    currency: "MXN",
    minimumFractionDigits: 2,
  }).format(n);
}

function formatDate(t: number | null) {
  if (!t) return "—";
  return new Date(t).toLocaleString("es-MX", {
    day: "2-digit",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function ChangeDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [order, setOrder] = useState<ChangeOrderDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showApprove, setShowApprove] = useState(false);
  const [showReject, setShowReject] = useState(false);
  const [signatureSvg, setSignatureSvg] = useState<string | null>(null);
  const [signatureName, setSignatureName] = useState("");
  const [rejectionReason, setRejectionReason] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const load = async () => {
    if (!id) return;
    setIsLoading(true);
    setError(null);
    try {
      const data = await clientApiService.getChangeOrder(id);
      setOrder(data);
    } catch (err: any) {
      setError(err?.message || "Error al cargar orden");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleApprove = async () => {
    if (!order || !signatureSvg || !signatureName.trim()) {
      setActionError("Firma y nombre completo son requeridos");
      return;
    }
    if (signatureName.trim().length < 3) {
      setActionError("El nombre debe tener al menos 3 caracteres");
      return;
    }

    setIsSubmitting(true);
    setActionError(null);
    try {
      const updated = await clientApiService.approveChangeOrder(order.id, {
        signatureSvg,
        signatureName: signatureName.trim(),
      });
      setOrder(updated);
      setShowApprove(false);
      setSignatureSvg(null);
      setSignatureName("");
    } catch (err: any) {
      setActionError(err?.message || "Error al aprobar");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReject = async () => {
    if (!order || !rejectionReason.trim()) {
      setActionError("Debes indicar un motivo");
      return;
    }
    setIsSubmitting(true);
    setActionError(null);
    try {
      const updated = await clientApiService.rejectChangeOrder(
        order.id,
        rejectionReason.trim()
      );
      setOrder(updated);
      setShowReject(false);
      setRejectionReason("");
    } catch (err: any) {
      setActionError(err?.message || "Error al rechazar");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-4">
        {error || "Orden no encontrada"}
        <Link to="/dashboard/changes" className="ml-3 underline font-medium">
          ← Volver a cambios
        </Link>
      </div>
    );
  }

  const status = STATUS_LABELS[order.status] || {
    label: order.status,
    className: "bg-slate-100 text-slate-700",
  };
  const canAct = order.status === "pending";

  return (
    <div className="space-y-6">
      <Link
        to="/dashboard/changes"
        className="text-sm text-slate-500 hover:text-primary inline-flex items-center gap-1"
      >
        ← Volver a órdenes de cambio
      </Link>

      {/* Header */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-2">
              {order.number && (
                <span className="text-xs font-mono bg-slate-100 text-slate-700 px-2 py-0.5 rounded">
                  {order.number}
                </span>
              )}
              <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${status.className}`}>
                {status.label}
              </span>
              <span className="text-xs text-slate-500">
                Prioridad: {PRIORITY_LABELS[order.priority] || order.priority}
              </span>
            </div>
            <h1 className="text-2xl font-bold text-slate-900">
              {order.title || "Solicitud de cambio"}
            </h1>
            <Link
              to={`/dashboard/projects/${order.projectId}`}
              className="text-sm text-primary hover:underline font-medium mt-1 inline-block"
            >
              {order.projectName}
            </Link>
            <p className="text-xs text-slate-500 mt-2">
              Solicitada por {order.requestedByName || "—"} el {formatDate(order.createdAt)}
            </p>
          </div>

          <div className="text-right shrink-0">
            <p className="text-xs text-slate-500">Impacto económico</p>
            <p
              className={`text-3xl font-bold ${
                order.costImpact >= 0 ? "text-primary" : "text-green-600"
              }`}
            >
              {order.costImpact >= 0 ? "+" : ""}
              {formatCurrency(order.costImpact)}
            </p>
          </div>
        </div>

        <div className="mt-5 pt-5 border-t border-slate-100">
          <p className="text-slate-700 whitespace-pre-wrap">{order.description}</p>
        </div>

        {/* Resumen del impacto */}
        {order.impact && order.impact.originalBudget !== undefined && (
          <div className="mt-5 pt-5 border-t border-slate-100 grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
            <div className="bg-slate-50 rounded-lg p-3">
              <p className="text-xs text-slate-500">Presupuesto original</p>
              <p className="font-bold text-slate-900">
                {formatCurrency(order.impact.originalBudget || 0)}
              </p>
            </div>
            <div className="bg-slate-50 rounded-lg p-3">
              <p className="text-xs text-slate-500">Cambio neto</p>
              <p className="font-bold text-primary">
                {order.impact.impact !== undefined && order.impact.impact >= 0 ? "+" : ""}
                {formatCurrency(order.impact.impact || 0)}
              </p>
            </div>
            <div className="bg-slate-50 rounded-lg p-3">
              <p className="text-xs text-slate-500">Nuevo presupuesto</p>
              <p className="font-bold text-slate-900">
                {formatCurrency(order.impact.newBudget || 0)}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Partidas */}
      {order.items.length > 0 && (
        <div className="bg-white rounded-2xl border border-slate-200 p-6">
          <h2 className="text-lg font-bold text-slate-900 mb-4">
            Desglose de partidas
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-slate-100 text-xs text-slate-500">
                <tr className="text-left">
                  <th className="py-2 pr-3">Descripción</th>
                  <th className="py-2 px-3 text-right">Cant.</th>
                  <th className="py-2 px-3">Unidad</th>
                  <th className="py-2 px-3 text-right">P. unitario</th>
                  <th className="py-2 px-3 text-center">Tipo</th>
                  <th className="py-2 pl-3 text-right">Total</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {order.items.map((it, i) => (
                  <tr key={i}>
                    <td className="py-2 pr-3 text-slate-800">{it.description}</td>
                    <td className="py-2 px-3 text-right">{it.quantity}</td>
                    <td className="py-2 px-3 text-slate-500">{it.unit}</td>
                    <td className="py-2 px-3 text-right">
                      {formatCurrency(it.unitPrice)}
                    </td>
                    <td className="py-2 px-3 text-center">
                      <span
                        className={`text-xs px-2 py-0.5 rounded-full ${
                          it.type === "addition"
                            ? "bg-blue-100 text-blue-700"
                            : it.type === "deduction"
                            ? "bg-green-100 text-green-700"
                            : "bg-slate-100 text-slate-700"
                        }`}
                      >
                        {it.type === "addition"
                          ? "Suma"
                          : it.type === "deduction"
                          ? "Resta"
                          : "Modificación"}
                      </span>
                    </td>
                    <td
                      className={`py-2 pl-3 text-right font-medium ${
                        it.type === "deduction" ? "text-green-600" : "text-slate-900"
                      }`}
                    >
                      {it.type === "deduction" ? "-" : ""}
                      {formatCurrency(it.total)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Adjuntos */}
      {order.attachments.length > 0 && (
        <div className="bg-white rounded-2xl border border-slate-200 p-6">
          <h2 className="text-lg font-bold text-slate-900 mb-4">Adjuntos</h2>
          <ul className="space-y-2">
            {order.attachments.map((att, i) => (
              <li key={i}>
                <a
                  href={att.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition"
                >
                  <span className="text-lg">📎</span>
                  <span className="text-sm text-slate-700 truncate flex-1">
                    {att.name}
                  </span>
                  <span className="text-xs text-primary font-medium">
                    Abrir →
                  </span>
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Estado aprobación/rechazo */}
      {order.clientApprovedAt && (
        <div className="bg-green-50 border border-green-200 rounded-2xl p-5">
          <h3 className="font-bold text-green-800 mb-2">✅ Aprobado por ti</h3>
          <p className="text-sm text-green-700">
            Firmado por <strong>{order.clientSignatureName}</strong> el{" "}
            {formatDate(order.clientApprovedAt)}
          </p>
        </div>
      )}
      {order.clientRejectedAt && (
        <div className="bg-red-50 border border-red-200 rounded-2xl p-5">
          <h3 className="font-bold text-red-800 mb-2">❌ Rechazado por ti</h3>
          <p className="text-sm text-red-700">
            El {formatDate(order.clientRejectedAt)}
          </p>
          {order.rejectionReason && (
            <p className="text-sm text-red-700 mt-1">
              Motivo: <strong>{order.rejectionReason}</strong>
            </p>
          )}
        </div>
      )}

      {/* Acciones */}
      {canAct && !showApprove && !showReject && (
        <div className="bg-white rounded-2xl border border-slate-200 p-6">
          <h3 className="font-bold text-slate-900 mb-2">Acciones</h3>
          <p className="text-sm text-slate-500 mb-4">
            Revisa los detalles con cuidado. Tu decisión afectará el presupuesto del proyecto.
          </p>
          <div className="flex gap-3 flex-wrap">
            <button
              onClick={() => {
                setShowApprove(true);
                setShowReject(false);
                setActionError(null);
              }}
              className="px-5 py-2.5 bg-green-600 hover:bg-green-700 text-white rounded-lg font-semibold transition"
            >
              ✅ Aprobar cambio
            </button>
            <button
              onClick={() => {
                setShowReject(true);
                setShowApprove(false);
                setActionError(null);
              }}
              className="px-5 py-2.5 bg-red-50 hover:bg-red-100 text-red-700 rounded-lg font-semibold transition"
            >
              ❌ Rechazar cambio
            </button>
          </div>
        </div>
      )}

      {/* Panel aprobar */}
      {showApprove && (
        <div className="bg-white rounded-2xl border border-slate-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold text-slate-900">Firmar aprobación</h3>
            <button
              onClick={() => {
                setShowApprove(false);
                setSignatureSvg(null);
                setSignatureName("");
                setActionError(null);
              }}
              disabled={isSubmitting}
              className="text-slate-400 hover:text-slate-700 text-xl disabled:opacity-50"
            >
              ×
            </button>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4 text-sm text-blue-800">
            ℹ️ Al firmar aceptas el cambio propuesto con un impacto económico de{" "}
            <strong>{formatCurrency(order.costImpact)}</strong>.
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1.5">
                Nombre completo <span className="text-primary">*</span>
              </label>
              <input
                type="text"
                value={signatureName}
                onChange={(e) => setSignatureName(e.target.value)}
                placeholder="Tu nombre completo"
                maxLength={120}
                disabled={isSubmitting}
                className="w-full px-4 py-3 border-2 border-slate-200 rounded-lg bg-slate-50 focus:bg-white focus:border-primary outline-none"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1.5">
                Firma <span className="text-primary">*</span>
              </label>
              <SignaturePad
                onChange={(svg) => setSignatureSvg(svg)}
                height={180}
              />
            </div>

            {actionError && (
              <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg p-3">
                {actionError}
              </div>
            )}

            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowApprove(false);
                  setSignatureSvg(null);
                  setSignatureName("");
                  setActionError(null);
                }}
                disabled={isSubmitting}
                className="px-4 py-2 border border-slate-200 rounded-lg text-slate-700 font-medium hover:bg-slate-50 transition disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                onClick={handleApprove}
                disabled={isSubmitting || !signatureSvg || !signatureName.trim()}
                className="px-6 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-semibold transition disabled:opacity-50"
              >
                {isSubmitting ? "Firmando..." : "✅ Confirmar aprobación"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Panel rechazar */}
      {showReject && (
        <div className="bg-white rounded-2xl border border-slate-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold text-slate-900">Rechazar cambio</h3>
            <button
              onClick={() => {
                setShowReject(false);
                setRejectionReason("");
                setActionError(null);
              }}
              disabled={isSubmitting}
              className="text-slate-400 hover:text-slate-700 text-xl disabled:opacity-50"
            >
              ×
            </button>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1.5">
                Motivo del rechazo <span className="text-primary">*</span>
              </label>
              <textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                rows={4}
                maxLength={500}
                placeholder="Explica brevemente por qué rechazas este cambio..."
                disabled={isSubmitting}
                className="w-full px-4 py-3 border-2 border-slate-200 rounded-lg bg-slate-50 focus:bg-white focus:border-primary outline-none resize-none"
              />
            </div>

            {actionError && (
              <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg p-3">
                {actionError}
              </div>
            )}

            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowReject(false);
                  setRejectionReason("");
                  setActionError(null);
                }}
                disabled={isSubmitting}
                className="px-4 py-2 border border-slate-200 rounded-lg text-slate-700 font-medium hover:bg-slate-50 transition disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                onClick={handleReject}
                disabled={isSubmitting || !rejectionReason.trim()}
                className="px-6 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-semibold transition disabled:opacity-50"
              >
                {isSubmitting ? "Procesando..." : "Rechazar"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}